// ════════════════════════════════════════════════════════════════════════════
//  Ciclos de contagem ABC (Cycle Counting) — CRUD + agenda
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, CalendarClock, Pencil, Trash2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiClient';
import { cycleService as svc, type CicloContagem, type SalvarCicloRequest } from '@/features/wms/wmsService';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ABC = ['Todos', 'A', 'B', 'C'];
const SENT = '__none__';
const dt = (s: string | null) => s ? new Date(s).toLocaleDateString('pt-BR') : '—';

export default function CiclosContagem() {
  const qc = useQueryClient();
  const { data: ciclos = [] } = useQuery({ queryKey: ['ciclos-contagem'], queryFn: () => svc.listarCiclos() });
  const { data: locais = [] } = useLocalEstoqueCombo();
  const [editing, setEditing] = useState<CicloContagem | 'new' | null>(null);

  const inval = () => qc.invalidateQueries({ queryKey: ['ciclos-contagem'] });
  const excluir = useMutation({
    mutationFn: (id: number) => svc.excluirCiclo(id),
    onSuccess: () => { toast.success('Ciclo excluído'); inval(); }, onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Ciclos de Contagem (ABC)</h1>
          <p className="text-muted-foreground mt-1">Programe auditorias recorrentes por classe ABC e local; gere contagens a partir deles.</p>
        </div>
        <Button className="gap-2" onClick={() => setEditing('new')}><Plus className="h-4 w-4" />Novo Ciclo</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5" />Ciclos</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>Classe</TableHead><TableHead>Local</TableHead>
              <TableHead className="text-right">Freq. (dias)</TableHead><TableHead>Último</TableHead><TableHead>Próximo</TableHead>
              <TableHead>Ativo</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!ciclos.length ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nenhum ciclo cadastrado</TableCell></TableRow>
              ) : ciclos.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.nome}</TableCell>
                  <TableCell><Badge variant="secondary">{c.classificacaoAbc}</Badge></TableCell>
                  <TableCell>{c.localEstoque ?? (c.localEstoqueId === 0 ? 'Todos' : c.localEstoqueId)}</TableCell>
                  <TableCell className="text-right">{c.frequenciaDias}</TableCell>
                  <TableCell>{dt(c.dtUltimoCiclo)}</TableCell>
                  <TableCell>{dt(c.dtProximoCiclo)}</TableCell>
                  <TableCell>{c.isAtivo ? <Badge className="bg-green-500/10 text-green-600">Ativo</Badge> : <Badge variant="secondary">Inativo</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(c)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => excluir.mutate(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <CicloDialog ciclo={editing === 'new' ? null : editing} locais={locais}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); inval(); }} />
      )}
    </div>
  );
}

function CicloDialog({ ciclo, locais, onClose, onSaved }: {
  ciclo: CicloContagem | null; locais: { id: number; descricao: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<SalvarCicloRequest>({
    nome: ciclo?.nome ?? '', classificacaoAbc: ciclo?.classificacaoAbc ?? 'Todos',
    frequenciaDias: ciclo?.frequenciaDias ?? 30, localEstoqueId: ciclo?.localEstoqueId ?? 0, isAtivo: ciclo?.isAtivo ?? true,
  });
  const salvar = useMutation({
    mutationFn: () => ciclo ? svc.atualizarCiclo(ciclo.id, form) : svc.criarCiclo(form),
    onSuccess: () => { toast.success('Ciclo salvo'); onSaved(); }, onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const set = (p: Partial<SalvarCicloRequest>) => setForm({ ...form, ...p });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{ciclo ? 'Editar' : 'Novo'} Ciclo</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome</Label><Input value={form.nome} onChange={e => set({ nome: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Classe ABC</Label>
              <Select value={form.classificacaoAbc} onValueChange={(v) => set({ classificacaoAbc: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ABC.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Frequência (dias)</Label><Input type="number" value={form.frequenciaDias} onChange={e => set({ frequenciaDias: Number(e.target.value) })} /></div>
          </div>
          <div><Label>Local</Label>
            <Select value={form.localEstoqueId ? String(form.localEstoqueId) : SENT} onValueChange={(v) => set({ localEstoqueId: v === SENT ? 0 : Number(v) })}>
              <SelectTrigger><SelectValue placeholder="Todos os locais" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SENT}>Todos os locais</SelectItem>
                {locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="flex items-center gap-2"><Switch checked={form.isAtivo} onCheckedChange={(c) => set({ isAtivo: c })} /><Label>Ativo</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!form.nome || salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
